/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import React, { FunctionComponent, useEffect, useRef } from 'react';

import { Carousel as CarouselData } from 'client-code/wrs-feed-service';

import { InteractiveComponents } from '../../../core/enums';
import { Locale } from '../../../core/locale/Locale';
import { LocaleKeys } from '../../../core/locale/LocaleKeys';
import { Caret } from '../../common';
import Tile from '../Tile/Tile';
import TileDetailPanel from '../TileDetailPanel/TileDetailPanel';

// Layout constants — must match SCSS values
const TILE_WIDTH_VW = 22.24; // tile width (427px)
const TILE_GAP_VW = 1.56; // gap between tiles
const TOTAL_TILE_STEP_VW = TILE_WIDTH_VW + TILE_GAP_VW; // scroll unit per tile
const RECENTLY_WATCHED_CAROUSEL_NAME = 'kaveriRecentlyWatched';

interface CarouselProps {
    carousel: CarouselData;
    carouselIndex: number;
    focusedCarouselIndex: number;
    focusedTileIndex: number;
    previewData: object | null;
    imageHostBaseUrl: string;
    navBarFocused?: boolean;
    /** Ref callback for the highlight frame element (used for home-screen video-window playback) */
    onHighlightFrameRef?: (el: HTMLDivElement | null) => void;
    /** True when home-screen video-window playback is actively running on this carousel's highlighted tile */
    isHomeScreenVideoWindowPlaying?: boolean;
    ghostCarouselIndex: number | null;
    ghostTileIndex: number | null;
    ghostScrollOffset: number;
    isGhostTarget?: boolean;
    activeInteractiveComponent?: string;
    /** Title of the carousel positioned above this one (shown when this carousel is focused) */
    previousCarouselTitle?: string;
}

const CarouselComponent: FunctionComponent<CarouselProps> = ({
    carousel,
    carouselIndex,
    focusedCarouselIndex,
    focusedTileIndex,
    previewData,
    imageHostBaseUrl,
    navBarFocused,
    onHighlightFrameRef,
    isHomeScreenVideoWindowPlaying = false,
    ghostCarouselIndex,
    ghostTileIndex,
    ghostScrollOffset,
    isGhostTarget,
    activeInteractiveComponent,
    previousCarouselTitle,
}): JSX.Element => {
    const isActiveCarousel = carouselIndex === focusedCarouselIndex && !navBarFocused;
    // Ghost frame needs tileIndex to know where to draw the border
    const isGhostCarousel = carouselIndex === ghostCarouselIndex && ghostTileIndex !== null;
    const feedItems = carousel.feedItems?.feedItems ?? [];
    const isLastTile = isActiveCarousel && focusedTileIndex >= feedItems.length - 1;

    // Determine header behaviour when home-screen video-window playback is active
    const videoWindowActive = isActiveCarousel && isHomeScreenVideoWindowPlaying;
    const showNowPlaying = videoWindowActive;
    const hideHeader = videoWindowActive && isLastTile;

    // EAST/WEST caret visibility — only on the carousel that has actual focus
    const tilesPerPage = 4;
    const canScrollWest = isActiveCarousel && isGhostTarget === true && ghostScrollOffset > 0;
    const canScrollEast =
        isActiveCarousel &&
        isGhostTarget === true &&
        feedItems.length > tilesPerPage &&
        ghostScrollOffset < feedItems.length - 1;

    const tileListRef = useRef<HTMLDivElement>(null);
    // Preserve the last scroll position so inactive carousels don't visually reset
    const lastScrollIndexRef = useRef(0);

    // Horizontal scroll: translateX the tile strip so the focused tile sits at the left edge.
    // When caret-driven ghost scroll is active on this carousel, ghostScrollOffset takes precedence.
    useEffect(() => {
        if (!isActiveCarousel) return;
        const listEl = tileListRef.current;
        if (!listEl) return;

        if (isGhostTarget && ghostScrollOffset > 0) {
            const offset = ghostScrollOffset * TOTAL_TILE_STEP_VW;
            listEl.style.transform = `translateX(-${offset}vw)`;
            lastScrollIndexRef.current = ghostScrollOffset;
        } else {
            const offset = focusedTileIndex * TOTAL_TILE_STEP_VW;
            listEl.style.transform = `translateX(-${offset}vw)`;
            lastScrollIndexRef.current = focusedTileIndex;
        }
    }, [isActiveCarousel, focusedTileIndex, isGhostTarget, ghostScrollOffset]);

    // Ghost scroll: apply separate translateX when a non-active carousel is ghost-hovered
    useEffect(() => {
        if (isActiveCarousel) return; // active carousel scroll is handled above
        const listEl = tileListRef.current;
        if (!listEl) return;

        const isGhostHovered = carouselIndex === ghostCarouselIndex;
        if (isGhostHovered && ghostScrollOffset > 0) {
            const offset = ghostScrollOffset * TOTAL_TILE_STEP_VW;
            listEl.style.transform = `translateX(-${offset}vw)`;
        } else {
            // Maintain last scroll position instead of resetting to 0
            const offset = lastScrollIndexRef.current * TOTAL_TILE_STEP_VW;
            listEl.style.transform = `translateX(-${offset}vw)`;
        }
    }, [isActiveCarousel, carouselIndex, ghostCarouselIndex, ghostScrollOffset]);

    return (
        <div className="carousel">
            {isActiveCarousel && previousCarouselTitle && (
                <div className="carousel-previous-title">{previousCarouselTitle}</div>
            )}
            <div className="carousel-inner">
                <div className="carousel-header-row">
                    {showNowPlaying && (
                        <div className="carousel-now-playing">
                            <span className="carousel-now-playing__label">
                                {Locale.getString(LocaleKeys.HOME_SCREEN_NOW_PLAYING)}
                            </span>
                        </div>
                    )}
                    {!hideHeader && (
                        <div
                            className={`carousel-header${
                                isActiveCarousel ? ' carousel-header--active' : ''
                            }`}
                        >
                            {carousel.caption}
                        </div>
                    )}
                </div>
                <div className="tile-list-wrapper">
                    {/* Fixed highlight frame — pinned to the left edge */}
                    {isActiveCarousel && (
                        <div
                            className="tile-list-highlight-frame"
                            ref={(el) => {
                                onHighlightFrameRef?.(el);
                            }}
                        />
                    )}
                    {isGhostCarousel &&
                        (() => {
                            let scrollBase: number;
                            if (isActiveCarousel && isGhostTarget && ghostScrollOffset > 0) {
                                scrollBase = ghostScrollOffset;
                            } else if (isActiveCarousel) {
                                scrollBase = focusedTileIndex;
                            } else {
                                // Use preserved scroll position for inactive carousels so
                                // the ghost frame aligns with the visually-stable tile strip.
                                scrollBase = ghostScrollOffset || lastScrollIndexRef.current;
                            }
                            return (
                                <div
                                    className="ghost-highlight-frame"
                                    style={{
                                        transform: `translateX(${((ghostTileIndex ?? 0) - scrollBase) * TOTAL_TILE_STEP_VW}vw)`,
                                    }}
                                />
                            );
                        })()}
                    <div className="tile-list" ref={tileListRef}>
                        {feedItems.map((feedItem, tileIndex) => (
                            <Tile
                                key={`${carouselIndex}-${tileIndex}-${feedItem.feedItemId ?? ''}`}
                                feedItem={feedItem}
                                isFocused={isActiveCarousel && tileIndex === focusedTileIndex}
                                imageHostBaseUrl={imageHostBaseUrl}
                                isChannelThumbnail={
                                    carousel.carouselName === RECENTLY_WATCHED_CAROUSEL_NAME
                                }
                                carouselIndex={carouselIndex}
                                tileIndex={tileIndex}
                            />
                        ))}
                    </div>
                    {canScrollWest && (
                        <Caret
                            direction={InteractiveComponents.WEST}
                            visible
                            active={activeInteractiveComponent === InteractiveComponents.WEST}
                            className="carousel-caret-west"
                        />
                    )}
                    {canScrollEast && (
                        <Caret
                            direction={InteractiveComponents.EAST}
                            visible
                            active={activeInteractiveComponent === InteractiveComponents.EAST}
                            className="carousel-caret-east"
                        />
                    )}
                </div>
            </div>
            {isActiveCarousel && (
                <div className="carousel-detail-row">
                    <TileDetailPanel
                        previewData={previewData}
                        visible
                        isLoading={previewData == null}
                        feedItemTitle={feedItems[focusedTileIndex]?.title}
                    />
                    <div className="carousel-tip-text">
                        {Locale.getString(LocaleKeys.GUIDE_SELECT_PRESS_AND_HOLD_MESSAGE)}
                    </div>
                </div>
            )}
        </div>
    );
};

export default CarouselComponent;
